import {
    COMPARE_AT,
    DISCOUNTED,
    ORIGINAL,
    SUBSCRIPTION_ONLY
} from './constants.js';

export default class Product {
    constructor({ data = {}, selectors, props, container }) {
        this.data = data;
        this.selectors = selectors;
        this.props = props;
        this.container = container;

        this.variants = this.data.variants;
        this.id = this.data.external_product_id;
        this.subscriptionOnly =
            this.data.subscription_options?.storefront_purchase_options ===
                SUBSCRIPTION_ONLY || false;
        this.sellingPlanGroups = this.data.selling_plan_groups;
    }

    DISCOUNTED = DISCOUNTED;
    ORIGINAL = ORIGINAL;
    COMPARE_AT = COMPARE_AT;

    getPrice(variantId, priceType) {
        try {
            return this.prices[variantId][priceType];
        } catch (e) {
            window.console.warn('ReCharge: Price missing for variant');
            window.console.log(`Variant ID: ${variantId}`);
            window.console.log(`Type: ${priceType}`);
            window.console.log(this.prices);
            return {};
        }
    }

    get sellingPlans() {
        const groups = this.sellingPlanGroups;
        return (groups && groups[0] && groups[0].selling_plans) || [];
    }

    get prices() {
        /*
            Return price breakdown by variant ID
            [
                123456: { 
                    ORIGINAL: 5000, 
                    DISCOUNTED: 4350,
                    COMPARTE_AT: 5000
                },
                ...
            ]
        */
        const variants = this.variants;
        const sellingPlans = this.sellingPlans;

        return variants.reduce((accum, variant) => {
            const { id, prices } = variant;

            accum[id] = { [this.ORIGINAL]: prices.unit_price };

            if (sellingPlans.length) {
                accum[id][this.DISCOUNTED] =
                    prices.discounted_price || prices.unit_price;
                accum[id][this.COMPARE_AT] =
                    prices.compare_at_price || prices.unit_price;
            }

            return accum;
        }, {});
    }

    get variantOptions() {
        /*
            Return the variant options found within the product container
            - If options aren't found, return hidden variant element
        */

        let options = this.container.querySelectorAll(
            this.selectors.variantOptions
        );

        return options.length
            ? options
            : this.container.querySelectorAll('[name="id"]');
    }

    get currentOptionValues() {
        /*
            Build an object containing all active option values. This may seem
            a strange way to determine the active product, but appears to be
            the popular method by many theme developers, including Shopify
        */
        let result = [];

        this.variantOptions.forEach(option => {
            const type = option.getAttribute('type');
            const isRadioOrCheckbox = type === 'radio' || type === 'checkbox';
            const isButton = type === 'button';

            if (isButton && option.getAttribute('data-value')) {
                // We may want to consider replacing the attribute with the actual button text
                result.push({
                    value: option.getAttribute('data-value')
                });
            } else if (!isRadioOrCheckbox || option.checked) {
                // If radio or chekbox, only filter active options
                result.push({
                    value: option.value
                });
            }
        });

        return result;
    }

    get initialVariantId() {
        /*
            Get the initial variant ID
            - We should default to the first variant
            - In Shopify, we'll want to check the URL for the URL param "variant"
        */
        return this.variants[0].id;
    }

    get activeVariant() {
        /*
            Search the variants list for a matching option combination.
            This assumes the options are in order, which is likely due 
            to the chronological nature that they're generated.
            If only 1 variant exists, return it
        */
        if (this.variants.length === 1) {
            return this.variants[0];
        }

        const optionValues = this.currentOptionValues;
        const variant = this.variants.find(variant => {
            return optionValues.every((option, i) => {
                /* 
                    Match variant with ID
                    - Assumes Python will return options in order
                    - May require additional engineering otherwise
                */
                return (
                    variant.option_values[i]['label'].toLowerCase() ==
                    option.value.toLowerCase()
                );
            });
        });

        if (variant) return variant;

        // Variant combination search failed, try first option value
        return this.variants.find(
            variant => variant.id === parseInt(optionValues[0].value)
        );
    }
}
